import { projectTypeValues } from "../../standardEntities/project";
import { formatDate } from "../dateUtils";
import { translatePicklistValue } from "../fieldsUtils";
import { nullSafeString } from "../stringUtils";
import { locatorTypeValues } from "../../standardEntities/locator";
import { safeConvertToFixed } from "../numberUtils";
import { AbstractPDFContentGenerator, AssembledPdfData } from "./PDFContentGenerator";
import { allOutTransactionTypeValues, receiverDocTypeValues, senderDocTypeValues } from "../../standardEntities/materialOutward";
import { materialTypeValues, transportModeValues } from "../../standardEntities/materialInward";
import { getReceiverProjectDetails, getSenderProjectDetails } from "./pdfContentUtils";
import { isEmptyObj } from "../utils";

export class GatePassPdfHandler extends AbstractPDFContentGenerator {
  getRecordType(): string {
    return "gate-pass";
  }

  assemblePdfData(record: any): AssembledPdfData {
    let projectDetailsHandler = getStoreTransferProjectDetailsHandler(record);
    let title = [["GATE PASS"]];
    let senderReceiverData = [
      [
        `Gate Pass No: ${record.id} \nGate Pass Date: ${formatDate(record.docDate)} \n\nSender Material: ${translatePicklistValue(
          record.docMaterialType,
          materialTypeValues
        )}\nSender Doc: ${translatePicklistValue(record.senderDocType, senderDocTypeValues)}\nSender Doc No: ${nullSafeString(
          record.senderDocNo
        )} \nSender Doc Date: ${formatDate(record.senderDocDate)} `,
        `Sender: ${record.docOwnerName} (${record.docOwnerCode}) \n${record.docAddress1} \n${nullSafeString(record.docAddress2)} \nStation: ${
          record.docStationName
        } (${record.docStateName}) \t\tGST No: ${nullSafeString(record.docGSTNo ?? record.receiverGSTNo)} \nLocator: ${record.docLocatorName} (${
          record.docLocatorCode
        }) \t\tLocator Type: ${translatePicklistValue(record.docLocatorType, locatorTypeValues)} \nVendor: ${nullSafeString(
          record.docCompanyName
        )} (${nullSafeString(record.docCompanyCode)}) \n${projectDetailsHandler.getMasterSenderProjectDetails(record)}`,
      ],
      [
        `MOA No: ${record.moaId} \nMOA Date: ${formatDate(record.moaDate)} \n\nRecv Material: ${translatePicklistValue(
          nullSafeString(record.receiverMaterialType ?? record.docMaterialType),
          materialTypeValues
        )}\nRecv Doc: ${translatePicklistValue(record.receiverDocType, receiverDocTypeValues)} \nRecv Doc No: ${nullSafeString(
          record.receiverDocNo
        )}\nRecv Doc Date: ${formatDate(record.receiverDocDate)}`,
        `Receiver: ${
          record.receiverLocatorType === "CS" || record.receiverLocatorType === "PS" ? record.receiverOwnerName : record.receiverCompanyName
        } (${record.receiverLocatorType === "CS" || record.receiverLocatorType === "PS" ? record.receiverOwnerCode : record.receiverCompanyCode}) \n${
          record.receiverAddress1
        } \n${nullSafeString(record.receiverAddress2)}\nStation: ${record.receiverStationName} (${
          record.receiverStateName
        }) \t\tGST No: ${nullSafeString(record.receiverGSTNo)} \nLocator: ${record.receiverLocatorName} (${
          record.receiverLocatorCode
        }) \t\tLocator Type: ${translatePicklistValue(record.receiverLocatorType, locatorTypeValues)} \nVendor: ${nullSafeString(
          record.receiverCompanyName
        )} (${nullSafeString(record.receiverCompanyCode)}) \n${projectDetailsHandler.getMasterReceiverProjectDetails(record)} `,
      ],
    ];

    let totalDetailQuantity = record.details.reduce((total, detail) => {
      return total + parseFloat(detail.totalQuantity);
    }, 0);
    totalDetailQuantity = Math.round(totalDetailQuantity * 100) / 100;

    let dtHead = projectDetailsHandler.getDTHead();

    let details = projectDetailsHandler.getDetails(record, totalDetailQuantity);
    let remarks = [[`Remarks: ${nullSafeString(record.remarks)}`]];

    let otherValues = [
      [
        `Total Value: ${safeConvertToFixed(record.totalValue, 2)} \nTotal Packages: ${nullSafeString(
          record.totalPkgs
        )} \nStatus: ${this.getRecordStatus(record)}\t\t\t\t\t\t\t\t\t\t`,
        `Transport Mode: ${translatePicklistValue(record.transportMode, transportModeValues)} \nTransport Vendor: ${nullSafeString(
          record.vendorName
        )} (${nullSafeString(record.vendorCode)}) \nVehicle No: ${nullSafeString(record.vehicleNo)}\t\t\t\t\t\t\t\t\t\t`,
        `Consignment Note: ${nullSafeString(record.consignmentNote)}\nEway Bill: ${nullSafeString(record.ewayBillNo)}\nMIR Awaited: ${
          record.mirPending ? "Yes" : "No"
        }\t\t\t\t\t\t\t\t\t\t`,
      ],
      [
        `Delivery Date: ${formatDate(record.deliveryDate)}\t\t\t\t\t\t\t\t\t\t`,
        `Receiver Remarks: ${nullSafeString(record.receiverRemarks)}\t\t\t\t\t\t\t\t\t\t`,
        `${record.mirId ? "MIR No: " : ""}${nullSafeString(record.mirId)}\t\t\t\t\t\t\t\t\t\t`,
      ],
    ];

    return { title, senderReceiverData, dtHead, details, remarks, otherValues };
  }

  private getRecordStatus(record: any) {
    if (record.cancelled) {
      return "Cancelled";
    } else if (record.delivered) {
      return "Delivered";
    }
    return "Delivery Pending";
  }

  //=======================================================================================
}

interface StoreTransferSenderProjectDetails {
  getMasterSenderProjectDetails(record: any): string;
  getMasterReceiverProjectDetails(record: any): string;
  getDTHead(): string[][];
  getDetails(record: any, totalDetailQuantity: number): string[][];
}

class SingleProjectHandler implements StoreTransferSenderProjectDetails {
  getMasterSenderProjectDetails(record: any): string {
    return getSenderProjectDetails(record);
  }
  getMasterReceiverProjectDetails(record: any): string {
    return getReceiverProjectDetails(record);
  }
  getDTHead(): string[][] {
    return [["SN", "Description Of Material", "Part Code", "Out Qty", "UOM", "Value", "Item Id", "MIR No", "MIR Row"]];
  }

  getDetails(record: any, totalDetailQuantity: number): string[][] {
    let details = record.details.map((row, index) => {
      return [
        index + 1,
        row.itemName,
        row.itemCustCode,
        safeConvertToFixed(row.totalQuantity, 1),
        row.uom,
        safeConvertToFixed(row.totalAmount, 2),
        row.itemId,
        row.mirId,
        row.mirRowId,
      ];
    });

    details.push(["", "", "Totals", safeConvertToFixed(totalDetailQuantity, 1), "", safeConvertToFixed(record.totalValue, 2), "", "", ""]);
    return details;
  }
}

class MultiProjectHandler implements StoreTransferSenderProjectDetails {
  getMasterSenderProjectDetails(record: any): string {
    return `Project Type: ${translatePicklistValue(record.docProjectType, projectTypeValues)} \t\tTrans Type: ${translatePicklistValue(
      record.transactionType,
      allOutTransactionTypeValues
    )}`;
  }
  getMasterReceiverProjectDetails(record: any): string {
    return getReceiverProjectDetails(record);
  }
  getDTHead(): string[][] {
    return [
      [
        "SN",
        "Description Of Material",
        "Part Code",
        "Project Name",
        "Project",
        "Virtual Locator",
        "Out Qty",
        "UOM",
        "Value",
        "Item Id",
        "MIR No",
        "MIR Row",
      ],
    ];
  }

  getDetails(record: any, totalDetailQuantity: number): string[][] {
    let details = record.details.map((row, index) => {
      return [
        index + 1,
        row.itemName,
        row.itemCustCode,
        row.projectName,
        row.projectCode,
        row.projectVirtualLocator,
        safeConvertToFixed(row.totalQuantity, 1),
        row.uom,
        safeConvertToFixed(row.totalAmount, 2),
        row.itemId,
        row.mirId,
        row.mirRowId,
      ];
    });

    details.push([
      "",
      "",
      "",
      "",
      "",
      "Totals",
      safeConvertToFixed(totalDetailQuantity, 1),
      "",
      safeConvertToFixed(record.totalValue, 2),
      "",
      "",
      "",
    ]);
    return details;
  }
}

export const getStoreTransferProjectDetailsHandler = (record): StoreTransferSenderProjectDetails => {
  if (isEmptyObj(record.docProjectName)) {
    return new MultiProjectHandler();
  } else {
    return new SingleProjectHandler();
  }
};

const gatePassPdfHandler = new GatePassPdfHandler();
export default gatePassPdfHandler;
